"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const StatesContext = createContext();
export const useStates = () => useContext(StatesContext);

export const StatesProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);

  const mountedRef = useRef(true);

  const STATES_ENDPOINT = "/states";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetStates = (updater) => {
    if (mountedRef.current) setStates(updater);
  };

  /* ---------------- FETCH ---------------- */
  const fetchStates = useCallback(async () => {
    if (!token) return;

    setStatesLoading(true);

    try {
      const res = await fetch(`${baseURL}${STATES_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      safeSetStates(payload?.data || []);

      return payload;
    } catch (err) {
      console.error("[States] fetch error:", err);
      safeSetStates([]);
    } finally {
      if (mountedRef.current) setStatesLoading(false);
    }
  }, [token, baseURL]);

  /* ---------------- CREATE ---------------- */
  const addState = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${STATES_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchStates();
        return payload;
      } catch (err) {
        console.error("[States] create error:", err);
        throw err;
      }
    },
    [token, adminId, fetchStates]
  );

  /* ---------------- UPDATE ---------------- */
  const updateState = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${STATES_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchStates();
        return payload;
      } catch (err) {
        console.error("[States] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchStates]
  );

  /* ---------------- DELETE ---------------- */
  const deleteState = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${STATES_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetStates((prev) =>
          prev.filter((s) => String(s.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[States] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateStateStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(
          `${baseURL}${STATES_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        safeSetStates((prev) =>
          prev.map((s) =>
            String(s.id) === String(id) ? { ...s, status } : s
          )
        );

        return payload;
      } catch (err) {
        console.error("[States] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchStates();
  }, [isReady, token, fetchStates]);

  return (
    <StatesContext.Provider
      value={{
        states,
        statesLoading,
        fetchStates,
        addState,
        updateState,
        deleteState,
        updateStateStatus,
      }}
    >
      {children}
    </StatesContext.Provider>
  );
};